import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  TablePagination,
  InputAdornment,
  InputLabel,
  Tooltip
} from "@mui/material";
import { AiOutlineClose, AiOutlineSearch } from "react-icons/ai";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { PiFileCsv } from "react-icons/pi";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useQuery } from "@tanstack/react-query";
import { fetchMissingDocTypes } from "../../api/dashboardApi";
import dayjs from "dayjs";

const Row = ({ row }) => {
  const [open, setOpen] = useState(false);

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          "&:hover": { backgroundColor: "#fcf9ff" },
        }}
      >
        <TableCell sx={{ width: 50 }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            sx={{ color: "#7C2EA0" }}
          >
            {open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
          {row.doc_id}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, color: "#7C2EA0" }}>
          {row.doc_missing_discrepancy_total?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, color: "#7C2EA0" }}>
          {row.invoice_count}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 2,
                backgroundColor: "#fdfbff",
                borderRadius: "8px",
                p: 1,
                border: "1px solid #f0e6f5",
              }}
            >
              {/* <Typography
                variant="subtitle1"
                gutterBottom
                component="div"
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#7C2EA0",
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 16,
                    bgcolor: "#7C2EA0",
                    mr: 1,
                    borderRadius: 1,
                  }}
                />
                Impacted Invoices
              </Typography> */}
              <TableContainer sx={{ maxHeight: 250, overflow: "auto", borderRadius: "4px" }}>
                <Table size="small" aria-label="invoices" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", fontSize: "12px", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>
                        Invoice ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", fontSize: "12px", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>
                        Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", fontSize: "12px", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>
                        Account No
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", fontSize: "12px", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}
                      >
                        Missing Discrepancy
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", fontSize: "12px", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}
                      >
                        Discrepancy
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", fontSize: "12px", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}
                      >
                        Total Adjustment
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(row.invoices || []).map((invoice) => (
                      <TableRow
                        key={invoice.invoice_id}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell sx={{ fontSize: "12px" }}>
                          {invoice.invoice_id}
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          {invoice.invoice_date}
                        </TableCell>
                        <TableCell sx={{ fontSize: "12px" }}>
                          {invoice.account_no}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontSize: "12px", fontWeight: 500 }}
                        >
                          <Tooltip title="Discrepancy due to missing document" arrow placement="top">
                            <span>
                              {invoice.invoice_missing_discrepancy_total?.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontSize: "12px", fontWeight: 500 }}
                        >
                          <Tooltip title="Total discrepancy of that invoice." arrow placement="top">
                            <span>
                              {invoice.total_discrepancy?.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontSize: "12px", fontWeight: 500 }}
                        >
                          {invoice.total_adj?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const MissingDocTypesModal = ({
  open,
  onClose,
  initialFilters,
  totalMissingDiscrepancy,
  formatNumber,
  countryList = [],
  locationList = [],
  canViewLocation,
}) => {
  const [rangeType, setRangeType] = useState("all");
  const [dateRange, setDateRange] = useState({
    start: dayjs("2024-01-01"),
    end: dayjs(),
  });
  const [selectedCountry, setSelectedCountry] = useState(
    initialFilters.country || "All"
  );
  const [selectedLoc, setSelectedLoc] = useState(
    initialFilters.location_id || "All"
  );
  const [filteredLocations, setFilteredLocations] = useState(["All"]);

  // Search and Pagination states
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Set up filtered locations based on selected country
  useEffect(() => {
    if (selectedCountry === "All") {
      setFilteredLocations(["All"]);
    } else {
      const locList = ["All"];
      locationList?.forEach((el) => {
        const cntry = el?.updated_country || el?.country || "N/A";
        if (cntry.toLowerCase() === selectedCountry.toLowerCase()) {
          locList.push(el.location_id);
        }
      });
      setFilteredLocations(locList);
    }
  }, [selectedCountry, locationList]);

  // Handle range type changes
  useEffect(() => {
    if (rangeType === "custom") return;

    const today = dayjs();
    let start, end;

    switch (rangeType) {
      case "all":
        start = dayjs("2024-01-01");
        end = today;
        break;
      case "lastMonth":
        start = today.subtract(1, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      case "last3Months":
        start = today.subtract(3, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      case "last6Months":
        start = today.subtract(6, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      case "last12Months":
        start = today.subtract(12, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      default:
        start = dayjs("2024-01-01");
        end = today;
    }
    setDateRange({ start, end });
  }, [rangeType]);

  const handleReset = () => {
    setRangeType("all");
    const today = dayjs();
    setDateRange({
      start: dayjs("2024-01-01"),
      end: today,
    });
    setSelectedCountry("All");
    setSelectedLoc("All");
    setSearchQuery("");
    setPage(0);
  };

  const currentFilters = {
    start_date: dateRange.start.format("YYYY-MM-DD"),
    end_date: dateRange.end.format("YYYY-MM-DD"),
    vendor_id: initialFilters.vendor_id,
    location_id: selectedLoc,
    country: selectedCountry,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["missingDocTypes", currentFilters],
    queryFn: () => fetchMissingDocTypes(currentFilters),
    enabled: open,
  });

  const missingDocTypes = data?.data || [];

  const handleDownloadCSV = () => {
    if (!missingDocTypes.length) return;

    // Define columns for CSV
    const headers = ["Missing Document ID", "Total Missing Discrepancy", "Impacted Invoice Count"];
    const csvRows = [headers.join(",")];

    missingDocTypes.forEach(row => {
      const values = [
        `"${row.doc_id}"`,
        Number(row.doc_missing_discrepancy_total || 0).toFixed(2),
        row.invoice_count
      ];
      csvRows.push(values.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Pending_Documents_Breakdown_${dayjs().format("YYYYMMDD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter missingDocTypes based on search
  const filteredDocTypes = missingDocTypes.filter((doc) =>
    (doc.doc_id || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedDocTypes = filteredDocTypes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          Pending Document
          {missingDocTypes.length > 0 && (
            <Box
              component="span"
              sx={{
                backgroundColor: "#F8EAFF",
                color: "#7C2EA0",
                fontSize: "0.85rem",
                padding: "2px 10px",
                borderRadius: "12px",
                fontWeight: 600,
                border: "1px solid #7C2EA0",
              }}
            >
              {missingDocTypes.length}
            </Box>
          )}
          {totalMissingDiscrepancy > 0 && (
            <Tooltip 
              title={`Total Missing Discrepancy: ${(totalMissingDiscrepancy || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} 
              arrow 
              placement="top"
            >
              <Box
                component="span"
                sx={{
                  backgroundColor: "#fff",
                  color: "#7C2EA0",
                  fontSize: "0.85rem",
                  padding: "2px 10px",
                  borderRadius: "12px",
                  fontWeight: 600,
                  border: "1px solid #7C2EA0",
                }}
              >
                {formatNumber ? formatNumber(totalMissingDiscrepancy) : totalMissingDiscrepancy}
              </Box>
            </Tooltip>
          )}
        </Box>
        <IconButton onClick={onClose}>
          <AiOutlineClose />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* 🔹 Filter Bar */}
        <Box
          sx={{
            mb: 2,
            pt: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            justifyContent: "flex-end",
            flexWrap: "nowrap",
            overflowX: "auto",
            pb: 1,
            "&::-webkit-scrollbar": { height: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#e0e0e0",
              borderRadius: "10px",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 100, flexShrink: 0 }}>
              <InputLabel id="range-type-label" sx={{ fontSize: "13px" }}>Date</InputLabel>
              <Select
                labelId="range-type-label"
                label="Date"
                value={rangeType}
                onChange={(e) => setRangeType(e.target.value)}
                sx={{
                  fontSize: "13px",
                  height: "32px",
                  "& .MuiSelect-select": { py: "4px" },
                }}
              >
                <MenuItem value="all" sx={{ fontSize: "13px" }}>All</MenuItem>
                <MenuItem value="lastMonth" sx={{ fontSize: "13px" }}>Last Month</MenuItem>
                <MenuItem value="last3Months" sx={{ fontSize: "13px" }}>Last 3 Months</MenuItem>
                <MenuItem value="last6Months" sx={{ fontSize: "13px" }}>Last 6 Months</MenuItem>
                <MenuItem value="last12Months" sx={{ fontSize: "13px" }}>Last 12 Months</MenuItem>
                <MenuItem value="custom" sx={{ fontSize: "13px" }}>Custom Range</MenuItem>
              </Select>
            </FormControl>

            {rangeType === "custom" && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: 130,
                          "& .MuiInputBase-root": {
                            height: "32px",
                            fontSize: "13px",
                          },
                          "& .MuiInputBase-input": { py: "4px" },
                        },
                      },
                    }}
                    value={dateRange.start}
                    maxDate={dayjs()}
                    onChange={(v) => setDateRange({ ...dateRange, start: v })}
                  />
                  <DatePicker
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: 130,
                          "& .MuiInputBase-root": {
                            height: "32px",
                            fontSize: "13px",
                          },
                          "& .MuiInputBase-input": { py: "4px" },
                        },
                      },
                    }}
                    value={dateRange.end}
                    minDate={dateRange.start}
                    maxDate={dayjs()}
                    onChange={(v) => setDateRange({ ...dateRange, end: v })}
                  />
                </Stack>
              </LocalizationProvider>
            )}

            {canViewLocation && (
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Autocomplete
                  size="small"
                  options={countryList}
                  value={selectedCountry}
                  sx={{
                    width: 120,
                    "& .MuiInputBase-root": {
                      height: "32px",
                      fontSize: "13px",
                      py: 0,
                    },
                    "& .MuiInputBase-input": { fontSize: "13px" },
                    "& .MuiInputLabel-root": { fontSize: "13px", top: "-2px" },
                  }}
                  onChange={(e, val) => {
                    setSelectedCountry(val || "All");
                    setSelectedLoc("All");
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Country" size="small" />
                  )}
                />
                <Autocomplete
                  size="small"
                  options={filteredLocations}
                  value={selectedLoc}
                  disabled={selectedCountry === "All"}
                  sx={{
                    width: 120,
                    "& .MuiInputBase-root": {
                      height: "32px",
                      fontSize: "13px",
                      py: 0,
                    },
                    "& .MuiInputBase-input": { fontSize: "13px" },
                    "& .MuiInputLabel-root": { fontSize: "13px", top: "-2px" },
                  }}
                  onChange={(e, val) => setSelectedLoc(val || "All")}
                  renderInput={(params) => (
                    <TextField {...params} label="Location" size="small" />
                  )}
                />
              </Stack>
            )}
          </Box>

          {/* Search Field */}
          <TextField
            size="small"
            placeholder="Search Document ID..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            sx={{
              minWidth: 200,
              "& .MuiInputBase-root": { height: "32px", fontSize: "13px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AiOutlineSearch size={18} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              {(rangeType !== "all" ||
                selectedCountry !== "All" ||
                selectedLoc !== "All" ||
                searchQuery !== "") && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleReset}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "13px",
                    paddingInline: "10px",
                    height: "32px",
                  }}
                >
                  Reset
                </Button>
              )}
              
              <Tooltip title="Download CSV" placement="top" arrow>
                <span
                  onClick={handleDownloadCSV}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#f5f5f5",
                    cursor: missingDocTypes.length > 0 ? "pointer" : "default",
                    opacity: missingDocTypes.length > 0 ? 1 : 0.5,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (missingDocTypes.length > 0) {
                      e.currentTarget.style.backgroundColor = "#e0e0e0";
                    }
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                >
                  <PiFileCsv size={18} color="#333" />
                </span>
              </Tooltip>
            </Stack>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1, overflowX: "hidden" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
              <CircularProgress sx={{ color: "#7C2EA0" }} />
            </Box>
          ) : isError ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="error">
                {error?.message || "Failed to load missing document types."}
              </Typography>
            </Box>
          ) : filteredDocTypes.length > 0 ? (
            <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
              <TableContainer sx={{ maxHeight: "45vh" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", width: 50, zIndex: 11 }} />
                      <TableCell
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        Missing Document ID
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        Total Missing Discrepancy
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        Impacted Invoice Count
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDocTypes.map((row) => (
                      <Row key={row.doc_id} row={row} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredDocTypes.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          ) : (
            <Box sx={{ p: 8, textAlign: "center" }}>
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{ fontStyle: "italic" }}
              >
                No data available matching your search.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MissingDocTypesModal;
